import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Paper,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TERMS_DATA } from '../../content/terms';
import type { TermsType } from '../../types/terms';

// 라우트 파라미터 → TermsType 매핑
const TYPE_MAP: Record<string, TermsType> = {
  service: 'SERVICE_TERMS',
  privacy: 'PRIVACY_CONSENT',
  marketing: 'MARKETING_CONSENT',
  // TermsType 직접 값도 허용
  SERVICE_TERMS: 'SERVICE_TERMS',
  PRIVACY_CONSENT: 'PRIVACY_CONSENT',
  MARKETING_CONSENT: 'MARKETING_CONSENT',
  PURCHASE_CONFIRM: 'PURCHASE_CONFIRM',
};

const TermsPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();

  const termsType = type && TYPE_MAP[type] ? TYPE_MAP[type] : null;
  const termsData = termsType ? TERMS_DATA[termsType] : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            aria-label="뒤로가기"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
            {termsData?.title ?? '약관'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          {!termsType && (
            <Alert severity="warning" sx={{ fontSize: '1rem' }}>
              존재하지 않는 약관 페이지입니다.
            </Alert>
          )}

          {termsData && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 2, fontSize: '0.85rem' }}
              >
                버전 {termsData.version} · 시행일: {termsData.effectiveDate}
              </Typography>
              <Box
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.9,
                  color: 'text.primary',
                  '& h2': { fontSize: '1.15rem', fontWeight: 700, mt: 3, mb: 1 },
                  '& h3': { fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 0.5 },
                  '& p': { mb: 1.5 },
                  '& table': { width: '100%', borderCollapse: 'collapse', my: 2, fontSize: '0.9rem' },
                  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.5, py: 1, textAlign: 'left' },
                  '& th': { bgcolor: 'action.hover', fontWeight: 600 },
                  '& ul, & ol': { pl: 3, mb: 1.5 },
                  '& li': { mb: 0.5 },
                  '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, my: 1.5, color: 'text.secondary' },
                  '& strong': { fontWeight: 600 },
                  '& hr': { my: 3, border: 'none', borderTop: '1px solid', borderColor: 'divider' },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{termsData.content}</ReactMarkdown>
              </Box>
            </>
          )}
        </Paper>

        {/* 푸터 링크 안내 */}
        <Box sx={{ mt: 3, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { label: '이용약관', path: '/terms/SERVICE_TERMS' },
            { label: '개인정보처리방침', path: '/terms/PRIVACY_CONSENT' },
          ]
            .filter((item) => item.path !== `/terms/${type}`)
            .map((item) => (
              <Typography
                key={item.path}
                component="a"
                href={item.path}
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {item.label}
              </Typography>
            ))}
        </Box>
      </Container>
    </Box>
  );
};

export default TermsPage;
